import { Loader2 } from "lucide-react";
import { Separator } from "@/frontend/components/ui/separator";
import { useEhrClientIdsQuery, useClinicalWorkflowsQuery, useBackendConfigsQuery } from "@/frontend/queries/dashboard/settings";
import { useBillingStateQuery } from "@/frontend/queries/dashboard/billing";
import { EHR_PROVIDERS, EHR_PROVIDER_SECTIONS, EHR_SECTION_REQUIRED_FEATURE, ehrProviderMap } from "@/frontend/constants/ehr-providers";
import { EhrProviderTile } from "./EhrProviderTile";
import { EhrClientDetailView } from "./EhrClientDetailView";
import { EhrClinicalWorkflowDetailView } from "./EhrClinicalWorkflowDetailView";
import { EhrBackendDetailView } from "./EhrBackendDetailView";
import { SectionLockBanner } from "./SectionLockBanner";
import type { SavedRow, ClinicalWorkflowRow } from "@/frontend/types/ehr-clients";
import type { ClientsSectionProps } from "@/frontend/types/dashboard/settings";
import type { Feature } from "@/server/types/billing";

export function ClientsSection({ provider }: ClientsSectionProps) {
  const { data: clientIds, isLoading } = useEhrClientIdsQuery();
  const { data: billingState } = useBillingStateQuery();
  const features = billingState?.features ?? null;
  const isFeatureEnabled = (feat: Feature | undefined): boolean => {
    if (!feat) return true;
    return features ? features[feat] === true : true;
  };
  const { data: clinicalWorkflows, isLoading: isLoadingClinical } = useClinicalWorkflowsQuery(features?.clinical_workflows === true);
  const { data: backendConfigs, isLoading: isLoadingBackend } = useBackendConfigsQuery(features?.backend_services === true);

  const savedMap = new Map<string, SavedRow>(
    (clientIds ?? []).map((r) => [r.provider_type, r])
  );
  const clinicalMap = new Map<string, ClinicalWorkflowRow>(
    (clinicalWorkflows ?? []).map((r) => [r.provider_type, r])
  );
  const backendMap = new Map<string, SavedRow>(
    (backendConfigs ?? []).map((r) => [r.provider_type, r])
  );

  /** Saved config for a provider tile/detail, picking the right table by section. */
  const savedFor = (p: (typeof EHR_PROVIDERS)[number]) =>
    p.section === "clinical-workflows"
      ? clinicalMap.get(p.type)
      : p.section === "backend"
      ? backendMap.get(p.type)
      : savedMap.get(p.type);

  const anyLoading = isLoading || isLoadingClinical || isLoadingBackend;

  const resolvedProvider = provider ? ehrProviderMap.get(provider) : undefined;
  // Coming-soon providers aren't configurable; fall through to the list.
  const selectedProvider =
    resolvedProvider && "comingSoon" in resolvedProvider && resolvedProvider.comingSoon
      ? undefined
      : resolvedProvider;

  if (selectedProvider) {
    // Backend (bulk data) config holds no server-loaded rows yet — render directly.
    if (selectedProvider.section === "backend") {
      return <EhrBackendDetailView key={selectedProvider.type} provider={selectedProvider} />;
    }
    if (anyLoading) {
      return (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (selectedProvider.section === "clinical-workflows") {
      return (
        <EhrClinicalWorkflowDetailView
          key={selectedProvider.type}
          provider={selectedProvider}
          savedRow={clinicalMap.get(selectedProvider.type)}
        />
      );
    }
    return (
      <EhrClientDetailView
        key={selectedProvider.type}
        provider={selectedProvider}
        savedRow={savedMap.get(selectedProvider.type)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">Connections</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Enable an EHR to use your own Client ID. When disabled, Medblocks' credentials are used.
          </p>
        </div>
      </div>

      <Separator />

      {anyLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {EHR_PROVIDER_SECTIONS.map((section) => {
            const providers = EHR_PROVIDERS.filter((p) => p.section === section.key);
            if (providers.length === 0) return null;
            const requiredFeature = EHR_SECTION_REQUIRED_FEATURE[section.key];
            const locked = !isFeatureEnabled(requiredFeature);
            return (
              <div key={section.key} className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  {section.label}
                </p>
                {locked && <SectionLockBanner label={section.label} />}
                <div className={`flex flex-col gap-2${locked ? " opacity-60 pointer-events-none" : ""}`}>
                  {providers.map((p) => (
                    <EhrProviderTile
                      key={p.type}
                      provider={p}
                      savedRow={savedFor(p)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
